import {isRecoverable, type OffersStatus} from '../../../stores/offersStore';
import type {MembershipStatus} from '../../../stores/rewardsStore';
import type {SessionState} from '../../../stores/sessionState';
import type {OffersItemWithMflId} from '../../../transport/types';

export type OfferScreenState =
  | 'missing_param'
  | 'checking'
  | 'store_required'
  | 'sign_in_required'
  | 'offer'
  | 'loading'
  | 'not_ready_error'
  | 'unlocking'
  | 'not_ready_empty'
  | 'finished';

export type OfferScreenInputs = {
  offerParam?: string;
  sessionState: SessionState;
  hasSelectedStore: boolean;
  offersStatus: OffersStatus;
  data: OffersItemWithMflId[] | null;
  unlockingWindowOpen: boolean;
  /**
   * The foreground session probe is in flight and the screen has not settled
   * yet on this mount. The caller passes false once a settled state has shown.
   */
  isRevalidating: boolean;
  /** Spec § 5: who the viewer is, once the loyalty record says. */
  membershipStatus: MembershipStatus;
  /** The membership lookup is in flight. Holds the screen only while `unknown`. */
  isMembershipLoading: boolean;
};

export const findOffer = (data: OffersItemWithMflId[] | null, offerParam?: string) =>
  // DIS-1661: identity is contentId (`offerId` on the client model), which
  // every offer has. mflId is matched too so deep links published before this
  // change — they carry an MFL id in the same path segment — still resolve.
  data?.find(o => o.offerId === offerParam || (!!o.mflId && o.mflId === offerParam));

type Resolved = {state: OfferScreenState; offer?: OffersItemWithMflId};
type ResolvedWithViewer = Resolved & {viewer: MembershipStatus};

/** Spec § 5.2: first matching rule wins. */
const resolveIgnoringProbe = (i: OfferScreenInputs): Resolved => {
  if (!i.offerParam) return {state: 'missing_param'};
  if (i.sessionState === 'pending') return {state: 'checking'};
  if (!i.hasSelectedStore) return {state: 'store_required'};
  if (i.sessionState === 'none') return {state: 'sign_in_required'};
  const offer = findOffer(i.data, i.offerParam);
  if (offer) return {state: 'offer', offer};
  // DIS-1928: a confirmed non-member's empty list stays the join panel while a
  // reload is in flight. The loading short-circuit below would paint the
  // skeleton, which the screen then shows as Unlocking if the window is open.
  // A null list has not resolved yet, and an error is still S6.
  if (
    i.membershipStatus === 'not_member' &&
    i.offersStatus !== 'error' &&
    i.data !== null &&
    isRecoverable(i.data)
  ) {
    return {state: 'not_ready_empty'};
  }
  if (i.offersStatus === 'idle' || i.offersStatus === 'loading') return {state: 'loading'};
  if (i.offersStatus === 'error') return {state: 'not_ready_error'};
  if (isRecoverable(i.data)) {
    // DIS-1917 § 5: a confirmed non-member is never told offers are unlocking.
    if (i.unlockingWindowOpen && i.membershipStatus !== 'not_member') return {state: 'unlocking'};
    if (i.membershipStatus === 'unknown' && i.isMembershipLoading) return {state: 'loading'};
    return {state: 'not_ready_empty'};
  }
  return {state: 'finished'};
};

/**
 * Spec § 5.2, plus the final review's probe rule. A probe in flight turns only S3
 * (sign in) and S5 (loading) into S2 (checking). It never replaces S4 or S6–S9,
 * so an offer on screen stays on screen; a dead session still reaches S3
 * through passiveLogout when the probe ends.
 */
export const resolveOfferScreenState = (i: OfferScreenInputs): ResolvedWithViewer => {
  const viewer = i.membershipStatus;
  const resolved = resolveIgnoringProbe(i);
  if (i.isRevalidating && (resolved.state === 'sign_in_required' || resolved.state === 'loading')) {
    return {state: 'checking', viewer};
  }
  return {...resolved, viewer};
};
